import {
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  createChart,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
} from 'lightweight-charts';
import React from 'react';
import { polygonApi } from '../../../services/polygon.js';
import { useProfileNavigation } from '../hooks/useProfileNavigation.js';
import type { WidgetProps } from '../types/index.js';

const RANGES = ['1M', '3M', '6M', '1Y', 'YTD', '5Y'] as const;
type Range = typeof RANGES[number];

type PriceChartConfig = {
  ticker?: string;
  currency?: string; // e.g. "USD"
};


const DAYS_BY_RANGE: Record<Exclude<Range, 'YTD'>, number> = {
  '1M': 22,
  '3M': 66,
  '6M': 132,
  '1Y': 264,
  '5Y': 1320,
};

const daysForRange = (range: Range): number => {
  if (range === 'YTD') {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diffMs = now.getTime() - start.getTime();
    return Math.max(2, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }
  return DAYS_BY_RANGE[range as Exclude<Range, 'YTD'>];
};

// Converts ms->s only if needed
const toUtc = (t: number): UTCTimestamp =>
  (t > 10_000_000_000 ? Math.floor(t / 1000) : t) as UTCTimestamp;

const PriceChartWidgetComponent: React.FC<WidgetProps> = ({ widget }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<IChartApi | null>(null);
  const candleRef = React.useRef<ISeriesApi<'Candlestick'> | null>(null);

  const [range, setRange] = React.useState<Range>('3M');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const cfg = (widget.config as PriceChartConfig | undefined) ?? {};
  const rawTicker =
    (widget.inputs.find(i => i.id === 'ticker')?.value as string) ||
    cfg.ticker ||
    'AAPL';
  const ticker = React.useMemo(() => rawTicker.toUpperCase(), [rawTicker]);
  const currency = cfg.currency ?? 'USD';

  const { openProfileByTicker } = useProfileNavigation();

  const theme = useTheme();

  // Keep a currency formatter instance
  const currencyFmt = React.useMemo(
    () => new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }),
    [currency]
  );

  // Last price for header context
  const [lastPrice, setLastPrice] = React.useState<number | null>(null);
  const [lastChangePct, setLastChangePct] = React.useState<number | null>(null);

  // Create chart once
  React.useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true, // Let LWC handle ResizeObserver internally
      layout: {
        background: { color: theme.palette.background.default },
        textColor: theme.palette.text.secondary,
        // Hide TradingView attribution logo; we provide attribution in the app header
        attributionLogo: false,
      },
      watermark: {
        visible: false,
      },
      grid: {
        vertLines: { color: theme.palette.divider },
        horzLines: { color: theme.palette.divider },
      },
      timeScale: { rightOffset: 2, borderVisible: false },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      crosshair: { mode: CrosshairMode.Normal },
      localization: {
        priceFormatter: (p: number) => currencyFmt.format(p),
      },
    });

    const candlesticks = chart.addCandlestickSeries({
      upColor: theme.palette.success.main,
      downColor: theme.palette.error.main,
      borderUpColor: theme.palette.success.main,
      borderDownColor: theme.palette.error.main,
      wickUpColor: theme.palette.success.main,
      wickDownColor: theme.palette.error.main,
      lastValueVisible: true,
      priceLineVisible: false,
    });

    chartRef.current = chart;
    candleRef.current = candlesticks;

    return () => {
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
    };
  }, [
    theme.palette.background.default,
    theme.palette.text.secondary,
    theme.palette.divider,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.primary.main,
    currencyFmt,
  ]);

  // Load/set data when ticker or range changes
  React.useEffect(() => {
    const chart = chartRef.current;
    const series = candleRef.current;
    if (!chart || !series) return;

    let alive = true;

    const days = daysForRange(range);
    setLoading(true);
    setError(null);

    polygonApi
      .getHistoricalData(ticker, days)
      .then((raw) => {
        if (!alive) return;

        if (!raw || raw.length === 0) {
          series.setData([]);
          setLastPrice(null);
          setLastChangePct(null);
          return;
        }

        const data = raw.map((c) => ({
          time: toUtc(c.timestamp),
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        }));

        series.setData(data);
        chart.timeScale().fitContent();

        const last = raw[raw.length - 1];
        const prev = raw[raw.length - 2];
        setLastPrice(last?.close ?? null);
        setLastChangePct(
          last && prev ? ((last.close - prev.close) / prev.close) * 100 : null
        );
      })
      .catch((err: unknown) => {
        if (!alive) return;
        console.error('Failed to load price history', err);
        setError(err instanceof Error ? err.message : 'Failed to load price history');
        series.setData([]);
        setLastPrice(null);
        setLastChangePct(null);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [ticker, range]);

  // Use a stable label id tied to widget id to avoid MUI Select re-mount quirks
  const labelId = React.useMemo(() => `range-label-${widget.id}`, [widget.id]);

  const handleMouseDown = React.useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    event.stopPropagation();
  }, []);

  const handlePointerDown = React.useCallback<React.PointerEventHandler<HTMLElement>>((event) => {
    event.stopPropagation();
  }, []);

  const handleTickerClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.stopPropagation();
      openProfileByTicker(ticker);
    },
    [openProfileByTicker, ticker]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          <ButtonBase
            component="span"
            disableRipple
            onMouseDown={handleMouseDown}
            onPointerDown={handlePointerDown}
            onClick={handleTickerClick}
            sx={{
              font: 'inherit',
              color: 'inherit',
              px: 0.25,
              mx: -0.25,
              borderRadius: 1,
              textTransform: 'uppercase',
              '&:hover': { color: 'primary.main' },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            {ticker}
          </ButtonBase>{' '}
          Price
          {lastPrice != null && (
            <>
              {' • '}{currencyFmt.format(lastPrice)}
              {lastChangePct != null && (
                <> ({lastChangePct >= 0 ? '+' : ''}{lastChangePct.toFixed(2)}%)</>
              )}
            </>
          )}
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={range}
          onChange={(_e, val) => { if (val) setRange(val as Range); }}
          onPointerDown={handlePointerDown}
          onMouseDown={handleMouseDown}
          aria-label="Range"
        >
          {RANGES.map(r => (
            <ToggleButton key={r} value={r} aria-label={r} sx={{ px: 1.25 }}>
              {r}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box
        ref={containerRef}
        sx={{ position: 'relative', flex: 1, minHeight: 200 }}
        aria-busy={!chartRef.current || loading}
      >
        {(!chartRef.current || loading) && (
          <Skeleton
            variant="rectangular"
            sx={{ position: 'absolute', inset: 0 }}
          />
        )}
        {error && (
          <Stack
            spacing={1}
            sx={{
              position: 'absolute',
              inset: 0,
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export const PriceChartWidget = React.memo(PriceChartWidgetComponent);
